<template>
  <div
    class="reactive-card"
    @pointerenter="handlePointerEnter"
    @pointermove="handlePointerMove"
    @pointerleave="handlePointerLeave"
    ref="cardRef"
  >
    <div class="card-tilt" ref="tiltRef">
      <div class="card-glow"><div class="card-glow-spot"></div></div>
      <div class="card-content">
        <slot></slot>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from "vue";

const cardRef = ref(null);
const tiltRef = ref(null);

const MAX_TILT_DEG = 3;

let cachedRect = null;
let rafId = null;
let pendingEvent = null;

// Measure geometry strictly once on pointerenter to completely avoid layout thrashing.
// Stored in document coordinates (paired with pageX/pageY) so wheel-scrolling
// while hovered doesn't leave the rect stale.
const handlePointerEnter = (e) => {
  if (e.pointerType !== "mouse" || !cardRef.value) return;
  const r = cardRef.value.getBoundingClientRect();
  cachedRect = {
    left: r.left + window.scrollX,
    top: r.top + window.scrollY,
    width: r.width,
    height: r.height,
  };
  // Place the glow under the cursor before it fades in, so it doesn't
  // slide in from wherever it was last left.
  setGlow(e);
};

// Glow position in card-local px; consumed by .card-glow-spot's transform.
const setGlow = (e) => {
  const el = tiltRef.value;
  if (!el || !cachedRect) return;
  el.style.setProperty("--gx", `${(e.pageX - cachedRect.left).toFixed(1)}px`);
  el.style.setProperty("--gy", `${(e.pageY - cachedRect.top).toFixed(1)}px`);
};

// Coalesce high-frequency pointer movements to at most 1 GPU property write per frame.
// Touch/pen are skipped: tilt-follow is meaningless without a hovering cursor.
const handlePointerMove = (e) => {
  if (e.pointerType !== "mouse") return;
  pendingEvent = e;
  if (rafId) return;

  rafId = requestAnimationFrame(() => {
    rafId = null;
    const el = tiltRef.value;
    if (!el || !cachedRect || !pendingEvent) return;

    const x = (pendingEvent.pageX - cachedRect.left) / cachedRect.width - 0.5;
    const y = (pendingEvent.pageY - cachedRect.top) / cachedRect.height - 0.5;

    el.style.setProperty("--rx", `${(-y * MAX_TILT_DEG).toFixed(2)}deg`);
    el.style.setProperty("--ry", `${(x * MAX_TILT_DEG).toFixed(2)}deg`);
    el.style.setProperty("--ty", "-4px");
    el.style.setProperty("--s", "1.01");
    setGlow(pendingEvent);
  });
};

const handlePointerLeave = () => {
  if (rafId) {
    cancelAnimationFrame(rafId);
    rafId = null;
  }
  cachedRect = null;
  pendingEvent = null;

  const el = tiltRef.value;
  if (!el) return;
  el.style.setProperty("--rx", "0deg");
  el.style.setProperty("--ry", "0deg");
  el.style.setProperty("--ty", "0px");
  el.style.setProperty("--s", "1");
};
</script>

<style scoped>
/* Static wrapper with perspective for 3D tilt depth */
.reactive-card {
  position: relative;
  perspective: 1000px;
  overflow: visible;
  z-index: 1;
}

.reactive-card:hover {
  z-index: 20;
}

/* Card surface: GPU-accelerated CSS tilt and elevation */
.card-tilt {
  position: relative;
  padding: 2rem;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05));
  border: 1px solid rgba(255, 165, 0, 0.3);
  border-radius: 16px;
  box-shadow:
    0 4px 6px rgba(0, 0, 0, 0.1),
    0 0 20px rgba(255, 165, 0, 0.1);
  overflow: visible;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  /* Depth comes from the parent's `perspective`; no perspective() here. */
  transform: translate3d(0, var(--ty, 0px), 0)
    rotateX(var(--rx, 0deg))
    rotateY(var(--ry, 0deg))
    scale(var(--s, 1));
  transition:
    transform 0.18s cubic-bezier(0.2, 0.8, 0.2, 1),
    border-color 0.3s ease,
    box-shadow 0.3s ease;
}

/* Promote to a GPU layer only while hovered, rather than keeping every
   card permanently layerized. */
.reactive-card:hover .card-tilt {
  will-change: transform;
  border-color: rgba(255, 165, 0, 0.5);
  box-shadow:
    0 14px 28px rgba(0, 0, 0, 0.25),
    0 0 35px rgba(255, 165, 0, 0.25);
}

/* Glow clip area: fades in via composited opacity on hover and clips the
   moving spot to the card's rounded shape. */
.card-glow {
  position: absolute;
  inset: 0;
  border-radius: inherit;
  overflow: hidden;
  opacity: 0;
  transition: opacity 0.4s ease;
  pointer-events: none;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  z-index: 0;
}

/* Fixed-size gradient spot that follows the cursor via transform only:
   it's rasterized once and then just moved, never repainted. */
.card-glow-spot {
  --glow-size: 1500px;
  position: absolute;
  top: 0;
  left: 0;
  width: var(--glow-size);
  height: var(--glow-size);
  background: radial-gradient(
    circle,
    rgba(255, 165, 0, 0.25),
    transparent 65%
  );
  transform: translate(
    calc(var(--gx, 0px) - var(--glow-size) / 2),
    calc(var(--gy, 0px) - var(--glow-size) / 2)
  );
  transition: transform 0.12s linear;
}

.reactive-card:hover .card-glow {
  opacity: 1;
}

.reactive-card:hover .card-glow-spot {
  will-change: transform;
}

.card-content {
  position: relative;
  z-index: 1;
}

/* Touch devices: :hover sticks after a tap, so skip the hover lift/glow. */
@media (hover: none) {
  .reactive-card:hover .card-tilt {
    will-change: auto;
    border-color: rgba(255, 165, 0, 0.3);
    box-shadow:
      0 4px 6px rgba(0, 0, 0, 0.1),
      0 0 20px rgba(255, 165, 0, 0.1);
  }
  .reactive-card:hover .card-glow {
    opacity: 0;
  }
}
</style>
